// Small AbortController-backed fetch wrapper used by the embed.
//
// Browsers don't apply a default network timeout to fetch — a hung
// API or a customer on a flaky train Wi-Fi can stall the
// "Personalizar" overlay or the Add-to-Cart spinner indefinitely.
// Throwing fast lets the caller show a useful error instead of a
// loading spinner that never resolves.
//
// 10 s is comfortably above the 95th-percentile latency for our
// storefront endpoints (template lookup, design save, /cart/add.js)
// while short enough that the customer notices and can retry rather
// than walking away.

export const DEFAULT_FETCH_TIMEOUT_MS = 10_000;

/** Like `fetch` but aborts after `timeoutMs`.  On timeout the rejection
 *  is a DOMException with name 'TimeoutError' so callers can branch
 *  on it (vs other AbortError shapes) — but matching the message
 *  works in older browsers too. */
export async function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit = {},
  timeoutMs: number = DEFAULT_FETCH_TIMEOUT_MS,
): Promise<Response> {
  // Honour an externally-supplied AbortSignal too — combine it with
  // our timeout signal so cancelling either aborts the request.
  const controller = new AbortController();
  const timer = setTimeout(() => {
    controller.abort(new DOMException(`fetch timed out after ${timeoutMs} ms`, 'TimeoutError'));
  }, timeoutMs);

  const externalSignal = init.signal;
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort(externalSignal.reason);
    else externalSignal.addEventListener('abort', () => controller.abort(externalSignal.reason), { once: true });
  }

  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}
